import React from "react";

const LossAversionVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
This lesson shows how loss aversion shapes promotion decisions. People feel the pain of losing something more than the joy of gaining it, and managers react strongest to losing the quietly reliable 50 50 performer. It explains why growth often speeds up only when exit risk becomes real, and how to signal external options without threats. The key condition is value: loss aversion works only if your absence would create a real gap.
      </p>

    </div>
  );

const transcript = (
  <div className="pt-2 pb-6 space-y-5 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>Your brain has a quirk and you can use it in your favour to get promoted.</p>

    <p className="text-[#B00020]">
      If I gave you ₹100 right now, you'd feel good. Maybe a 5 on the{" "}
      <em>happy meter.</em>
    </p>

    <p className="text-[#B00020]">
      But if I took ₹100 away from you? You'd feel about twice as bad. <span className="text-[#323232]">That's an
      8 or 9 out of 10 on the pain scale.</span>
    </p>

    <p>
      This is called loss aversion, the invisible hand steering most human
      decisions.
    </p>

    <p>
      And here's the thing: your stakeholder feels it too. Especially when it
      comes to losing valuable people.
    </p>

    <p>
      Imagine you went to a hotel many years back. Decent food. Nice enough
      atmosphere. But nothing particularly memorable. You haven't been there
      in ages. You haven't even thought about going back.
    </p>

    <p>
      Then one day, out of the blue, your friend tells you:{" "}
      "That hotel is closing this weekend."
    </p>

    <p>
      And suddenly, boom. You feel this urge to go there one more time. Or you
      feel genuinely sad that it's closing. Even though you hadn't thought
      about that place in years!
    </p>

    <p>
      You feel the loss now. You didn't value it much when you had the option.
      But the moment it's being taken away? Suddenly it feels important.
    </p>

    <p>
      That limited-edition chocolate you bought in bulk? Or that impulsive{" "}
      “Only 2 left!” purchase? That's <strong>loss aversion</strong> in
      action, the fear of missing out or losing something triggers you faster
      than the promise of gaining it.
    </p>

    <p>
      In other words, <em>fear of loss beats logic of merit</em>, every time.
    </p>

    <p>This applies to your promotion as well.</p>

    <p>And here’s the nuance nobody talks about.</p>

    <p>
      Loss aversion isn’t triggered by the top performer, they’re already
      getting promoted.
      <br />
      And it’s not triggered by the bottom performer, losing them actually
      feels like a win.
    </p>

    <p>
      Loss aversion hits hardest with the <strong>50–50 person.</strong>
      <br />
      The quietly solid one. The “not a superstar, but definitely not
      replaceable” one.
      <br />
      The one who keeps things running without noise, without drama, without
      needing babysitting.
    </p>

    <p>That’s the person whose exit creates panic.</p>

    <p>
      Right now, your manager probably takes you for granted. Not in a mean
      way, you're just part of the reliable machinery. You deliver. Things
      work. No drama.
    </p>

    <p>
      But the moment they sense you might leave? Everything changes.
    </p>

    <p>
      Suddenly, they're doing math in their head. The brain doesn’t operate on
      logic here, it operates on <em>default mode psychology:</em>
      <br />
      <p>
      <strong>
        “If I lose them, the disruption will be painful… much more painful
        than the effort of promoting them.”
      </strong>
    </p>
    </p>

    <p>
      It’s irrational. It’s subconscious.
      <br />
      And it’s exactly why managers suddenly fight to retain someone they
      never fought to grow.
    </p>

    <p>
      Now, I'm not saying you should threaten to quit. That's clumsy and can
      backfire badly. But giving a <em>subtle hint</em> that you're considering
      options? That's strategic.
    </p>

    <p>There’s 3 ways you can do this:</p>

    <ol className="list-decimal list-inside space-y-2 pl-5">
      <li>
        <strong>Handle it professionally:</strong> Say,{" "}
          "I've been approached with some interesting opportunities elsewhere,
          but I'd prefer to grow here if I can take on more responsibility."
        <br />
        You're signaling value (others want you) + loyalty (you prefer to stay)
        + clear need (more responsibility).
      </li>

      <li>
        <strong>Express genuine concern:</strong>{" "}
          "I'm eager to continue contributing at a higher level, and I want to
          make sure I'm on the right path here. Otherwise, I may have to
          consider other options."
        <br />
        You're framing it as wanting clarity, not making threats. You're giving
        them a chance to act.
      </li>

      <li>
        <strong>Talk about growth:</strong>{" "}
          "If I don't get the chance to take on bigger projects soon, I might
          have to explore options that align with my career growth goals."
        <br />
        You're making it about growth, not dissatisfaction. It's
        forward-looking, not complaining.
      </li>
    </ol>

    <p>
      The key is to frame it positively. Show commitment to the organization.
      Express desire to grow <em>there</em>. But make the alternative clear
      without being threatening.
    </p>

    <p>
      And never make it an ultimatum. That’s like lighting a match in a room
      full of fireworks.
    </p>

    <p>
      But here's the thing, and this is critical:
    </p>

    <p>
      Loss aversion only works if they believe they have something to lose. And
      that only happens if your boss actually values you, not necessarily loves
      you, not your biggest fan, but{" "}
      <strong>
        recognises your contribution enough that your absence creates a gap.
      </strong>
    </p>

    <p>
      If they don't actually value your contribution, hinting that you might
      leave won't trigger anything. They might just say{" "}
      "okay, good luck."
    </p>

    <p>
      So before you drop any hints, you need to build that value. You need to
      make yourself genuinely difficult to replace.
    </p>

    <p>
      Be the asset they guard, not the name they can trade.
    </p>
  </div>
);


  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default LossAversionVideo;
